/**
 * @file pages/RecurringPage.js
 * @description Customer Butler — recurring/maintenance plans. The repeat-revenue
 * dashboard: what's DUE soon up top, all plans below. "Mark complete" advances
 * the next-due date and logs to the property's service history.
 */
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Navigate } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import { useTheme } from '../hooks/useTheme';
import { Modal } from '../design-system';
import { apiGet, apiSend } from '../lib/api';

function money(n) { return n != null && n !== '' ? `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : ''; }
function daysUntil(dateStr) { return Math.round((new Date(`${String(dateStr).slice(0, 10)}T00:00:00`) - new Date().setHours(0, 0, 0, 0)) / 86400000); }

function RecurringPage() {
  const auth = useContext(AuthContext);
  const { theme } = useTheme();
  const [plans, setPlans] = useState([]);
  const [due, setDue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [all, dueSoon] = await Promise.all([
        apiGet('/customer/recurring'),
        apiGet('/customer/recurring/due?withinDays=60'),
      ]);
      setPlans(all);
      setDue(dueSoon);
    } catch (e) { setPlans([]); setDue([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function complete(id) {
    await apiSend(`/customer/recurring/${id}/complete`, 'POST', {});
    load();
  }

  if (!auth.isAuthenticated) return <Navigate to="/login" />;

  function Row({ p, highlight }) {
    const d = daysUntil(p.nextDueAt);
    const overdue = d < 0;
    return (
      <div style={{ ...R.row, ...(highlight ? R.rowDue : {}) }}>
        <div style={{ flex: 1 }}>
          <strong>{p.name}</strong>
          {p.status !== 'active' && <span style={R.pill}>{p.status}</span>}
          <div style={R.meta}>
            Every {p.intervalMonths} mo · next {String(p.nextDueAt).slice(0, 10)}
            {' '}<span style={{ color: overdue ? '#DC2626' : 'var(--text-tertiary,#9CA3AF)' }}>({overdue ? `${-d}d overdue` : `in ${d}d`})</span>
            {p.price != null ? ` · ${money(p.price)}` : ''}
          </div>
        </div>
        <button style={R.btnSmall} onClick={() => complete(p.id)}>Mark Done</button>
      </div>
    );
  }

  return (
    <div className="page-content" style={{ padding: '1rem', maxWidth: 900, margin: '0 auto' }}>
      <div style={R.header}>
        <h2 style={{ margin: 0 }}>Recurring Jobs</h2>
        <button style={{ ...R.btnPrimary, backgroundColor: theme.accentColor }} onClick={() => setShowAdd(true)}>+ Add Plan</button>
      </div>

      {loading ? <div style={R.muted}>Loading…</div> : (
        <>
          <div style={R.sectionTitle}>Due in the next 60 days {due.length > 0 && <span style={R.badge}>{due.length}</span>}</div>
          {due.length === 0 ? <div style={R.muted}>Nothing due soon. 🎉</div> : due.map((p) => <Row key={p.id} p={p} highlight />)}

          <div style={{ ...R.sectionTitle, marginTop: '1.5rem' }}>All plans ({plans.length})</div>
          {plans.length === 0 ? (
            <div style={R.empty}><h3>No recurring plans yet</h3><p style={{ color: 'var(--text-secondary,#666)' }}>Add a maintenance plan (e.g. septic pumping every 3 years) so repeat work never slips.</p></div>
          ) : plans.map((p) => <Row key={p.id} p={p} />)}
        </>
      )}

      <Modal isOpen={showAdd} onClose={() => setShowAdd(false)} title="Add Recurring Plan">
        <AddPlanForm onCancel={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />
      </Modal>
    </div>
  );
}

function AddPlanForm({ onCancel, onSaved }) {
  const [form, setForm] = useState({ name: 'Septic Pumping', customerId: '', intervalMonths: 36, nextDueAt: '', price: '', notes: '' });
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { apiGet('/customer/customers?limit=100').then(setCustomers).catch(() => setCustomers([])); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  async function submit(e) {
    e.preventDefault(); setSaving(true); setError(null);
    const body = { ...form, intervalMonths: parseInt(form.intervalMonths) || 36 };
    if (body.price === '') delete body.price;
    if (!body.nextDueAt) delete body.nextDueAt;
    const r = await apiSend('/customer/recurring', 'POST', body);
    setSaving(false);
    if (r.ok) onSaved(); else setError((r.error && r.error.message) || 'Failed to add plan');
  }
  return (
    <form onSubmit={submit} style={{ padding: '1rem' }}>
      {error && <div style={R.error}>{error}</div>}
      <div style={R.field}><label style={R.label}>Plan Name *</label><input style={R.input} value={form.name} onChange={(e) => set('name', e.target.value)} required /></div>
      <div style={R.field}><label style={R.label}>Customer *</label>
        <select style={R.input} value={form.customerId} onChange={(e) => set('customerId', e.target.value)} required>
          <option value="">— Select customer —</option>
          {customers.map((c) => <option key={c.id} value={c.id}>{c.displayName}</option>)}
        </select>
      </div>
      <div style={R.rowF}>
        <div style={R.field}><label style={R.label}>Interval (months)</label><input style={R.input} type="number" min="1" value={form.intervalMonths} onChange={(e) => set('intervalMonths', e.target.value)} /></div>
        <div style={R.field}><label style={R.label}>Next Due</label><input style={R.input} type="date" value={form.nextDueAt} onChange={(e) => set('nextDueAt', e.target.value)} /></div>
        <div style={R.field}><label style={R.label}>Price ($)</label><input style={R.input} type="number" step="0.01" value={form.price} onChange={(e) => set('price', e.target.value)} /></div>
      </div>
      <div style={R.field}><label style={R.label}>Notes</label><textarea style={{ ...R.input, minHeight: 50, resize: 'vertical' }} value={form.notes} onChange={(e) => set('notes', e.target.value)} /></div>
      <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary,#9CA3AF)', marginBottom: '0.75rem' }}>Leave "Next Due" blank to schedule it one interval from today.</div>
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button type="button" style={R.btnSecondary} onClick={onCancel}>Cancel</button>
        <button type="submit" disabled={saving} style={{ ...R.btnPrimary, opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : 'Add Plan'}</button>
      </div>
    </form>
  );
}

const R = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' },
  sectionTitle: { fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary, #6B7280)', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' },
  badge: { backgroundColor: '#FEF3C7', color: '#92400E', borderRadius: '9999px', padding: '0 0.5rem', fontSize: '0.7rem' },
  row: { display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', border: '1px solid var(--color-border, #E5E7EB)', borderRadius: '8px', marginBottom: '0.5rem', backgroundColor: 'var(--surface-card, #fff)' },
  rowDue: { borderColor: '#FBBF24', backgroundColor: '#FFFBEB' },
  meta: { fontSize: '0.8rem', color: 'var(--text-secondary, #6B7280)', marginTop: '0.2rem' },
  pill: { marginLeft: '0.5rem', fontSize: '0.65rem', padding: '1px 6px', borderRadius: '9999px', backgroundColor: 'var(--surface-page, #F3F4F6)', color: 'var(--text-secondary, #6B7280)', textTransform: 'capitalize' },
  muted: { color: 'var(--text-secondary, #666)', fontSize: '0.875rem', padding: '0.5rem 0' },
  empty: { textAlign: 'center', padding: '2rem', border: '2px dashed var(--color-border, #E5E7EB)', borderRadius: '8px' },
  rowF: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' },
  field: { flex: 1, minWidth: 130, marginBottom: '0.5rem' },
  label: { display: 'block', fontSize: '0.75rem', fontWeight: 500, marginBottom: '0.25rem', color: 'var(--text-secondary, #6B7280)' },
  input: { width: '100%', padding: '0.5rem', border: '1px solid var(--color-border, #E5E7EB)', borderRadius: '6px', fontSize: '0.875rem', backgroundColor: 'var(--surface-card, #fff)', color: 'var(--text-primary, #111)', boxSizing: 'border-box' },
  btnPrimary: { padding: '0.5rem 1rem', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, backgroundColor: 'var(--color-primary-600, #2563EB)', color: '#fff' },
  btnSecondary: { padding: '0.5rem 1rem', border: '1px solid var(--color-border, #E5E7EB)', borderRadius: '6px', backgroundColor: 'var(--surface-card, #fff)', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 },
  btnSmall: { padding: '0.4rem 0.75rem', border: '1px solid var(--color-primary-600, #2563EB)', borderRadius: '6px', backgroundColor: 'transparent', color: 'var(--color-primary-600, #2563EB)', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' },
  error: { backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.5rem 0.75rem', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem' },
};

export default RecurringPage;
